import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Route } from 'lucide-react'
import { useAuth } from '../store/auth'
import { getRoles, getPaths, getPathsByRole, assignPath, revokePathFromRole } from '../api/auth'
import { Badge } from '../components/Badge'
import { Button } from '../components/ui/button'
import type { Path } from '../types'

export function RolePaths() {
  const { roleId } = useParams<{ roleId: string }>()
  const navigate = useNavigate()
  const { user: me } = useAuth()
  const businessId = me!.businessId
  const qc = useQueryClient()

  const { data: roles = [] } = useQuery({
    queryKey: ['roles', businessId],
    queryFn: () => getRoles(businessId).then((r) => r.data),
  })

  const { data: allPaths = [] } = useQuery({
    queryKey: ['paths', businessId],
    queryFn: () => getPaths(businessId).then((r) => r.data),
  })

  const { data: rolePaths = [], isLoading } = useQuery({
    queryKey: ['role-paths', roleId],
    queryFn: () => getPathsByRole(roleId!).then((r) => r.data),
    enabled: !!roleId,
  })

  const role = roles.find((r: any) => r.id === roleId)

  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [original, setOriginal] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!isLoading) {
      const ids = new Set((rolePaths as Path[]).map((p) => p.id))
      setChecked(new Set(ids))
      setOriginal(new Set(ids))
    }
  }, [isLoading, rolePaths])

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toAssign = [...checked].filter((id) => !original.has(id))
  const toRevoke = [...original].filter((id) => !checked.has(id))
  const isDirty = toAssign.length > 0 || toRevoke.length > 0

  const save = useMutation({
    mutationFn: async () => {
      await Promise.all([
        ...toAssign.map((pathId) => assignPath(roleId!, pathId)),
        ...toRevoke.map((pathId) => revokePathFromRole(roleId!, pathId)),
      ])
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['role-paths', roleId] })
    },
  })

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => navigate('/roles')}
          className="flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink transition-colors"
        >
          <ArrowLeft size={14} /> Roles
        </button>
        <span className="text-ink-dim">/</span>
        <span className="text-sm font-medium text-ink">{role?.name ?? ''}</span>
        <span className="text-ink-dim">/</span>
        <span className="text-sm text-ink-dim">Paths</span>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">
            {role ? `Paths  ${role.name}` : 'Role Paths'}
          </h1>
          <p className="text-sm text-ink-dim mt-0.5">
            Check paths to grant access, uncheck to revoke. Save to apply changes.
          </p>
        </div>
        <Button onClick={() => save.mutate()} disabled={!isDirty} loading={save.isPending}>
          Save changes
        </Button>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
      ) : (allPaths as Path[]).length === 0 ? (
        <div className="p-8 text-center text-sm text-ink-dim flex flex-col items-center gap-2">
          <Route size={24} className="text-ink-dim" />
          <span>No paths defined for this business yet.</span>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-10" />
                <th>Method</th>
                <th>URL</th>
                <th className="hidden md:table-cell">Description</th>
              </tr>
            </thead>
            <tbody>
              {(allPaths as Path[]).map((p) => {
                const isChecked = checked.has(p.id)
                const wasOriginal = original.has(p.id)
                const changed = isChecked !== wasOriginal
                return (
                  <tr
                    key={p.id}
                    onClick={() => toggle(p.id)}
                    className={`cursor-pointer ${changed ? 'bg-accent-soft/60' : ''}`}
                  >
                    <td className="text-center">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggle(p.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="accent-accent w-4 h-4"
                      />
                    </td>
                    <td>
                      <Badge variant={p.method}>{p.method}</Badge>
                    </td>
                    <td className="font-mono text-xs text-ink">{p.url}</td>
                    <td className="text-ink-dim hidden md:table-cell">
                      {p.description ?? <span className="text-ink-dim/40"></span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {isDirty && (
        <div className="mt-3 text-xs text-ink-dim">
          {toAssign.length > 0 && <span className="text-emerald-600">{toAssign.length} to assign</span>}
          {toAssign.length > 0 && toRevoke.length > 0 && <span className="mx-2">·</span>}
          {toRevoke.length > 0 && <span className="text-red-500">{toRevoke.length} to revoke</span>}
        </div>
      )}
    </div>
  )
}
